import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  toApiError,
  useApi,
  useApiProblem,
  type ChannexQuarantineAvailabilityPreview,
} from '@/shared/api';

export function useReleaseNights() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation('revisionHolds');
  const [target, setTarget] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const preview = useQuery({
    queryKey: ['channex', 'availability-preview', target],
    queryFn: () => api.channex.quarantineAvailabilityPreview(target!),
    enabled: target !== null,
    retry: false,
    networkMode: 'always',
    staleTime: 0,
  });
  const problem = useApiProblem(preview.error);
  const release = useMutation({
    mutationFn: ({
      id,
      expected,
    }: {
      id: string;
      expected: ChannexQuarantineAvailabilityPreview;
    }) => api.channex.rederiveQuarantinedAvailability(id, expected),
    networkMode: 'always',
    onSuccess: async (result) => {
      setTarget(null);
      toast.success(result.enqueued > 0 ? t('queued', { count: result.enqueued }) : t('noWork'));
      await queryClient.invalidateQueries({ queryKey: ['channex'] });
    },
    onError: async (failure) => {
      const apiError = await toApiError(failure);
      setError(
        apiError.status === 0
          ? t('previewUnknown')
          : apiError.title === 'quarantine_availability_changed'
            ? t('previewChanged')
            : apiError.detail || apiError.title,
      );
      if (apiError.status !== 0) await preview.refetch();
    },
  });
  return {
    target,
    preview: preview.data,
    loading: preview.isFetching,
    error: error || (preview.isError ? problem?.detail || t('previewFailed') : null),
    ready: !!preview.data && !preview.isFetching && !preview.isError,
    retry: () => void preview.refetch(),
    open: (id: string) => {
      setError(null);
      setTarget(id);
    },
    close: () => {
      if (!release.isPending) setTarget(null);
    },
    confirm: (id: string) => {
      if (preview.data && !preview.isFetching && !preview.isError)
        release.mutate({ id, expected: preview.data });
    },
    pending: release.isPending,
  };
}
